import type { Options } from 'ky';

import type { JsonApiObject } from '@shared/helpers/jsonapi';
import { deserialize } from '@shared/helpers/jsonapi';

import type { ApiOptions } from './ApiProvider';
import Api from './ApiProvider';
import type {
  DeserializedSelfAllocationByPool,
  FcfsTransaction,
  LaunchpadProject,
  LaunchpadProjectListItem,
  LaunchpadProjectStatus,
  LaunchpadSalePool,
  SignedMessage,
} from './types';

export const getLaunchpadProjects = async (
  options?: ApiOptions<{
    searchParams?: {
      limit?: number;
      offset?: number;
      status?: LaunchpadProjectStatus;
    };
  }>,
) => {
  const data = await Api.get('launchpad/projects', options).json<JsonApiObject>();
  return deserialize<LaunchpadProjectListItem[]>(data);
};

export const getLaunchpadProject = async (id: string) => {
  const data = await Api.get(`launchpad/projects/${id}`).json<JsonApiObject>();
  return deserialize<LaunchpadProject>(data);
};

export const getLaunchpadProjectWithSales = async (id: string, options?: Options) => {
  const data = await Api.get(`launchpad/projects/${id}/pools`, options).json<JsonApiObject>();
  return deserialize<LaunchpadProject>(data);
};

export const getSelfLaunchpadProjectWithSales = async (id: string, options?: Options) => {
  const data = await Api.get(
    `launchpad/projects/${id}/pools-with-user-participation`,
    options,
  ).json<JsonApiObject>();
  return deserialize<LaunchpadProject>(data);
};

export const getLaunchpadSale = async (id: string) => {
  const data = await Api.get(`launchpad/pools/${id}`).json<JsonApiObject>();
  return deserialize<LaunchpadSalePool>(data, true);
};

export const getLaunchpadSales = async (id: string) => {
  return Api.get(`launchpad/pools`, {
    searchParams: {
      projectId: id,
    },
  }).json<{ data: LaunchpadSalePool[] }>();
};

export const depositLaunchpadFcfs = async (
  saleId: string,
  amount: number,
  isSqrpBasedOffchainTransactions: boolean,
  signedMessage?: SignedMessage,
) => {
  const data = await Api.post(`launchpad/pools/fcfs/${saleId}/transactions`, {
    json: {
      amount,
      sqrpBasedOffchainTransactions: isSqrpBasedOffchainTransactions,
      ...(signedMessage && { signedMessage }),
    },
  }).json<JsonApiObject>();
  return deserialize<FcfsTransaction>(data);
};

export const getFcfsAllocationsByPoolId = async (salePoolId: string) => {
  const data = await Api.get(
    `launchpad/pools/fcfs/${salePoolId}/my-allocation`,
  ).json<JsonApiObject>();
  return deserialize<DeserializedSelfAllocationByPool>(data);
};

export const depositLaunchpadSqrpGated = async (
  saleId: string,
  amount: number,
  isSqrpBasedOffchainTransactions: boolean,
  signedMessage?: SignedMessage,
) => {
  const data = await Api.post(
    `launchpad/pools/sqrp-gated/${saleId}/transactions
  `,
    {
      json: {
        amount,
        sqrpBasedOffchainTransactions: isSqrpBasedOffchainTransactions,
        ...(signedMessage && { signedMessage }),
      },
    },
  ).json<JsonApiObject>();
  return deserialize<FcfsTransaction>(data);
};

export const getSqrpGatedAllocationsByPoolId = async (salePoolId: string) => {
  const data = await Api.get(
    `launchpad/pools/sqrp-gated/${salePoolId}/my-allocation`,
  ).json<JsonApiObject>();
  return deserialize<DeserializedSelfAllocationByPool>(data);
};

export const depositLaunchpadProRataSqrpGated = async (
  saleId: string,
  amount: number,
  boosted: boolean,
  isSqrpBasedOffchainTransactions: boolean,
  signedMessage?: SignedMessage,
) => {
  const data = await Api.post(
    `launchpad/pools/pro-rata-sqrp-gated/${saleId}/transactions
  `,
    {
      json: {
        turboCharge: boosted,
        amount,
        sqrpBasedOffchainTransactions: isSqrpBasedOffchainTransactions,
        ...(signedMessage && { signedMessage }),
      },
    },
  ).json<JsonApiObject>();
  return deserialize<FcfsTransaction>(data);
};

export const getProRataSqrpGatedAllocationsByPoolId = async (salePoolId: string) => {
  const data = await Api.get(
    `launchpad/pools/pro-rata-sqrp-gated/${salePoolId}/my-allocation`,
  ).json<JsonApiObject>();
  return deserialize<DeserializedSelfAllocationByPool>(data);
};

export const depositLaunchpadProRata = async (
  saleId: string,
  amount: number,
  boosted: boolean,
  isSqrpBasedOffchainTransactions: boolean,
  signedMessage?: SignedMessage,
) => {
  const data = await Api.post(
    `launchpad/pools/pro-rata/${saleId}/transactions
  `,
    {
      json: {
        turboCharge: boosted,
        amount,
        sqrpBasedOffchainTransactions: isSqrpBasedOffchainTransactions,
        ...(signedMessage && { signedMessage }),
      },
    },
  ).json<JsonApiObject>();
  return deserialize<FcfsTransaction>(data);
};

export const getProRataAllocationsByPoolId = async (salePoolId: string) => {
  const data = await Api.get(
    `launchpad/pools/pro-rata/${salePoolId}/my-allocation`,
  ).json<JsonApiObject>();
  return deserialize<DeserializedSelfAllocationByPool>(data);
};

export const depositLaunchpadSqrp = async (saleId: string, amount: number) => {
  const data = await Api.post(
    `launchpad/pools/sqrp/${saleId}/transactions
  `,
    {
      json: { amount },
    },
  ).json<JsonApiObject>();
  return deserialize<FcfsTransaction>(data);
};
